import { Button } from '@/components/ui/button';
import { MapPin, Star, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

export const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/50 z-10" />
        <img
          // FIXED: Use the absolute path relative to the public folder
          src="/images/safari.webp" 
          alt="African Safari Sunset"
          loading="eager" // Hero images should load immediately
          className="w-full h-full object-cover animate-ken-burns" 
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-20 pt-24 pb-16">
        <div className="max-w-4xl">
          {/* Logo */}
          <div className="mb-8 animate-fadeInUp flex justify-center sm:justify-start">
            <img 
              src="/images/logo.png" 
              alt="Peregrine Tours & Safaris Logo" 
              className="w-32 h-32 sm:w-40 sm:h-40 object-contain drop-shadow-lg"
            />
          </div>

          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-primary/20 backdrop-blur-sm border border-primary/30 rounded-full px-4 py-2 mb-6 animate-fadeInUp">
            <Star className="w-4 h-4 text-primary" fill="currentColor" />
            <span className="text-sm font-medium text-white">Premier African Safari Experiences</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fadeInUp animation-delay-200">
            Discover the{' '}
            <span className="block mt-2">
              Wild Heart of Africa
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed animate-fadeInUp animation-delay-400">
            Embark on unforgettable journeys through Southern & Eastern Africa. From Victoria Falls to the Maasai Mara, we craft personalized safari adventures that celebrate nature, culture, and human connection.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fadeInUp animation-delay-600">
            <Button
              size="lg"
              onClick={() => scrollToSection('#destinations')}
              className="bg-primary hover:bg-primary-dark text-primary-foreground text-base px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              Explore Destinations
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 text-base px-8 py-6 rounded-lg transition-all duration-300"
            >
              <Link to="/contact">Plan Your Safari</Link>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-2xl animate-fadeInUp animation-delay-600">
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                <Users className="w-5 h-5 text-primary" />
                <span className="text-3xl font-bold text-white">500+</span>
              </div>
              <p className="text-sm text-white/80">Happy Travelers</p>
            </div>
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="text-3xl font-bold text-white">15+</span>
              </div>
              <p className="text-sm text-white/80">Destinations</p>
            </div>
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                <Star className="w-5 h-5 text-primary" fill="currentColor" />
                <span className="text-3xl font-bold text-white">4.9</span>
              </div>
              <p className="text-sm text-white/80">Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};